// The member (player) API client: /api/v1/member (registration plan step 2,
// docs/pages/member-accounts.md). A third client, separate from the public
// `api` and the staff `adminApi` / admFetch on purpose:
//
//   - it reads and writes ONLY `mcl_member_token`, never a staff key
//     (`mcl_admin_*`), and the staff clients never read this one, so the two
//     tokens can never cross (member-accounts.md quirk 4);
//   - it sends no X-League-Id and no ?sport= stamp: the member API resolves
//     the league from the host, like the public API;
//   - a 401 on a signed-in call ends the member session quietly (clears the
//     token, tells the listeners) and the call never settles, so no page shows
//     an error on the way out (plan decision 21, the same rule as the staff
//     session end). A 401 for a token that is no longer the stored one (a
//     newer sign-in in another tab) is a plain error. A 503 keeps the session.
//   - the registration detail (GET /registrations/:slug, step 6) sends the
//     token when one is stored but never answers 401: a refused token reads
//     as `session: 'ended'` (registration.md quirk 25). The client then ends
//     the member session the same way (clears the token, tells the listeners)
//     and still resolves with the data, so the page shows the sign-in panel,
//     never an error (decision 21). A newer token stored meanwhile by another
//     tab is never cleared.
//   - on a members-only league (registration step 33) the public client
//     (services/publicAccess.ts) reads the token through getMemberToken and,
//     when a public read answers 401 members_only `session_ended` for it,
//     ends the session through endMemberSessionFor (only while it is still
//     the stored token). Nothing else ever clears the token.
//
// No React and nothing from services/api.ts, sessionEnd.ts or AuthContext at
// runtime (the one import from services/api/types is type-only).
// The client never stores a token itself, not even the fresh one a password
// change returns (step 9): MemberAuthContext does, so each action has one
// storage owner.

import type { CustomFieldDef } from '@/services/api/types'
import { parseWaiverText } from '@/site/core/member/waiver'

const BASE = (import.meta.env.VITE_API_URL || '/api/v1') + '/member'
const TIMEOUT_MS = 30_000
/** The photo upload carries up to 20 MB and waits for a transcode slot. */
const PHOTO_TIMEOUT_MS = 120_000

export const MEMBER_TOKEN_KEY = 'mcl_member_token'

export function getMemberToken(): string | null {
  try {
    return localStorage.getItem(MEMBER_TOKEN_KEY)
  } catch {
    return null
  }
}

export function setMemberToken(token: string): void {
  try {
    localStorage.setItem(MEMBER_TOKEN_KEY, token)
  } catch {
    /* storage unavailable: the session lasts for this page only */
  }
}

export function clearMemberToken(): void {
  try {
    localStorage.removeItem(MEMBER_TOKEN_KEY)
  } catch {
    /* ignore */
  }
}

/** `accountBody` on the server (member-accounts.md › Data flow). */
export interface MemberAccount {
  id: number
  email: string
  email_verified_at: string | null
  last_sign_in_at: string | null
  created_at: string
}

/** `profileBody` on the server. */
export interface MemberProfile {
  id: number
  relation: 'self' | 'child'
  first_name: string
  last_name: string
  date_of_birth: string | null
  phone: string | null
  photo_url: string | null
}

/** PUT /me: only the keys present change; '' or null clears phone / date of birth. */
export interface MemberProfilePatch {
  first_name?: string
  last_name?: string
  phone?: string | null
  date_of_birth?: string | null
}

/**
 * A sign-up or sign-in that needs the email verified first (October 29,
 * 202): a 6-digit code was emailed; verifyEmail signs the person in.
 */
export interface MemberVerificationRequired {
  verification_required: true
  email: string
  /** "We sent a 6-digit code to …" (written for display). */
  message: string
}

export function isVerificationRequired(x: unknown): x is MemberVerificationRequired {
  return !!x && typeof x === 'object' && (x as { verification_required?: unknown }).verification_required === true
}

export interface MemberSession {
  token: string
  account: MemberAccount
  profile: MemberProfile | null
}

export interface MemberSignUpInput {
  first_name: string
  last_name: string
  email: string
  password: string
}

// ── Registrations (step 5's member routes; registration.md › Wire shapes) ──

/** Step 36: 'waitlist' (full, but the waitlist is on: can_register true and
 *  the POST makes a waitlisted entry) and 'waitlisted' (the member is on the
 *  waitlist; code already_waitlisted). */
export type MemberSignUpStateName = 'open' | 'registered' | 'closed' | 'not_yet_open' | 'not_available' | 'full' | 'waitlist' | 'waitlisted'

/** `sign_up` on a card: the server's verdict, with a member-facing message. */
export interface MemberSignUpState {
  state: MemberSignUpStateName
  can_register: boolean
  code: string | null
  message: string | null
  opens_at?: string | null
  reason?: 'team' | 'paid' | 'invite_only'
  entry?: { id: number; status: string; created_at: string | null }
}

export interface MemberRegistrationCard {
  slug: string
  name: string
  status: string
  participant: 'individual' | 'team'
  fee_cents: number
  opens_at: string | null
  closes_at: string | null
  capacity: number | null
  spots_left: number | null
  season: { label: string; sport_id: string | null }
  sign_up: MemberSignUpState
}

export interface MemberStandardQuestion {
  key: string
  label: string
  type: string
  required: boolean
  from: string
}

// ── Waivers (step 34's wire; the screens are step 35) ──

/** The attached waiver's current version, as every member read carries it
 *  (registration.md › Waivers › Member side). Plain text: render it as text,
 *  never as HTML (quirk 149). `key` is an opaque fingerprint of this exact
 *  text, echoed back when signing. */
export interface MemberWaiver {
  title: string
  version: number
  key: string
  /** The text ('' for a PDF version). */
  body: string
  /** A PDF version only (PDF waivers, migration 220): the file's public URL
   *  and size. Render the URL only as a link href that passed
   *  `waiver.ts › waiverPdfHref`. */
  pdf?: { url: string; bytes: number }
}

/** A signature: the typed full name, and the version and key of the text
 *  read; for a PDF version a member opened in this session, also when they
 *  opened it and that they ticked "I have read and agree" (the server needs
 *  both from a member signing a PDF; it records what the client reports). */
export interface MemberWaiverSign {
  name: string
  version: number
  key: string
  opened_at?: string
  agreed?: boolean
}

/** An entry's waiver state (sign-up.ts › memberWaiverState). `can_sign` and
 *  `due` are the server's: the page never re-derives who must sign. */
export interface MemberEntryWaiver {
  signed: boolean
  version: number | null
  signed_at: string | null
  current_version: number
  /** Unsigned, and not cancelled or rejected. */
  can_sign: boolean
  /** can_sign while pending or waiting for approval: the entry is held by the waiver. */
  due: boolean
}

/** Step 36: an entry's waitlist state (sign-up.ts › memberWaitlistState).
 *  The flags are the server's; the page never compares the dates itself. */
export interface MemberWaitlistState {
  /** 1-based place among the people waiting; null for an offer holder. */
  position: number | null
  offer_open: boolean
  offer_expires_at: string | null
  offer_expired: boolean
}

export interface MemberRegistrationEntryRef {
  id: number
  status: string
  created_at: string | null
  /** The server's memberCancelRefusal verdict (registration.md quirk 27). */
  can_cancel: boolean
  /** Only an invitation gets into this registration (the cancel's wording); optional for deploy skew. */
  invite_only?: boolean
  /** Step 12: the stored payment state ('none', 'pending', 'paid', 'refunded'). */
  payment_status: string
  /** Step 12: what the member still pays for this entry, or null (dues.ts › owes). */
  amount_due_cents: number | null
  /** QA, October 30: false while unpaid (first to pay gets the spot); optional for deploy skew. */
  spot_held?: boolean
  /** QA, October 30: why Pay would be refused now that no spot is left, else
   *  null: the checkout's own 409 (its message is written for display), and
   *  whether the member can join the waitlist instead. */
  pay_refusal?: { code: string; message: string; waitlist: boolean } | null
  /** Step 34: null without a waiver; optional for deploy skew. */
  waiver?: MemberEntryWaiver | null
  /** Step 36: null unless on (or dropped from) a waitlist; optional for deploy skew. */
  waitlist?: MemberWaitlistState | null
}

/** What the member pays for a priced registration (step 12; a T16
 *  whitelist: never the platform's margin). */
export interface MemberFeeQuote {
  price_cents: number
  service_fee_cents: number
  total_cents: number
}

export interface MemberRegistrationDetail {
  registration: MemberRegistrationCard & {
    /**
     * Step 21: a team registration only. Whether a team can be registered
     * from the page now (the server's teamSignUpState, the same rule POST
     * /registrations/:slug/teams throws). The page decides "Register a team"
     * from this, never from public_link (no wire carries it).
     */
    team_sign_up?: MemberSignUpState
    /** Step 12: the price lines, also for an anonymous reader; null when free. */
    fee_quote: MemberFeeQuote | null
    questions: { standard: MemberStandardQuestion[]; custom: CustomFieldDef[] }
    /** Step 34: the attached waiver's current text, or null; optional for deploy skew. */
    waiver?: MemberWaiver | null
  }
  session: 'none' | 'member' | 'ended'
  me: {
    entry: MemberRegistrationEntryRef | null
    /** The member's latest place here ended with their offer lapsing (QA, October 30); optional for deploy skew. */
    offer_lapsed?: boolean
    prefill: { first_name: string | null; last_name: string | null; email: string; phone: string | null }
  } | null
  /** The zone the league's games use, or null (show dates in the viewer's). */
  timezone: string | null
}

export interface MemberRegistrationList {
  registrations: MemberRegistrationCard[]
  timezone: string | null
}

/** `memberEntryWire` on the server. */
export interface MemberEntry {
  id: number
  status: string
  payment_status: string
  /** Step 12: what the member still pays for this entry, or null. */
  amount_due_cents: number | null
  first_name: string | null
  last_name: string | null
  email: string | null
  answers: Record<string, unknown>
  created_at: string | null
  cancelled_at: string | null
  cancelled_by: 'you' | 'league' | null
  can_cancel: boolean
  /** Step 23: a captain pays this team's fee (the player owes nothing). Optional for deploy skew. */
  captain_pays?: boolean
  /** Step 34: the entry's waiver state, null without a waiver; optional for deploy skew. */
  waiver?: MemberEntryWaiver | null
  /** Step 36: the entry's waitlist state, null when never waitlisted; optional for deploy skew. */
  waitlist?: MemberWaitlistState | null
  /** The entry's team (GET /me/entries only): its name (null until named) and the public team id once it's in the season. */
  team?: { name: string | null; team_id: number | null } | null
  registration: {
    slug: string
    name: string
    status: string
    participant: 'individual' | 'team'
    /** Only an invitation gets into this registration (the cancel's wording); optional for deploy skew. */
    invite_only?: boolean
    opens_at: string | null
    closes_at: string | null
    season: { label: string; sport_id: string | null }
  }
}

/** `memberOrderWire` on the server (registration.md › The member's order):
 *  what the member pays, never a platform field. `status` is the stored one. */
export interface MemberOrder {
  id: number
  /** 'pending' | 'paid' | 'expired' | 'refunded' (a string, as payment_status). */
  status: string
  /** QA, October 30: the payment arrived after the last spot went (it is refunded in full). Optional for deploy skew. */
  spot_taken?: boolean
  currency: string
  subtotal_cents: number
  service_fee_cents: number
  total_cents: number
  created_at: string | null
  expires_at: string | null
  paid_at: string | null
  registration: { slug: string; name: string }
  items: Array<{
    id: number
    kind: 'entry'
    entry_id: number | null
    first_name: string | null
    last_name: string | null
    amount_cents: number
    status: string
  }>
}

/**
 * GET /invites?token= (registration plan step 15, invites.ts ›
 * inviteLandingWire, a whitelist): the invitation and its registration.
 * Never says whether the invited address has an account (decision 19).
 */
export interface MemberInviteLanding {
  invite: {
    kind: 'player' | 'individual'
    /** Season import: 'finish' when the league already has the person on the team; missing from an older server (read as 'join'). */
    purpose?: 'join' | 'finish'
    email: string
    /** The address as shown to someone signed in as another one ("s•••@gmail.com"). */
    masked_email: string
    name: string | null
    expires_at: string
    invited_by: { name: string } | null
    league: { name: string }
    /** null for an individual invitation; `name` null for a team not named yet. */
    team: { name: string | null } | null
    registration: MemberRegistrationCard & {
      fee_quote: MemberFeeQuote | null
      questions: { standard: MemberStandardQuestion[]; custom: CustomFieldDef[] }
      /** Step 34: the attached waiver's current text, or null; optional for deploy skew. */
      waiver?: MemberWaiver | null
    }
  }
  timezone: string | null
}

/** GET /me/invites: one pending invitation of the signed-in member. `token`
 *  opens the join page: keep it out of the VM, every href and every log. */
export interface MemberInvitation {
  id: number
  kind: 'player' | 'individual'
  purpose: 'join' | 'finish'
  token: string
  expires_at: string | null
  invited_by: { name: string } | null
  team: { name: string | null } | null
  registration: { slug: string; name: string; season: { label: string; sport_id: string | null } }
}

export interface MemberInvitationList {
  invitations: MemberInvitation[]
  timezone: string | null
}

/**
 * GET /team-codes/:code (registration plan step 25, team-codes.ts ›
 * teamCodeLandingWire, a whitelist): the team a code joins, its captain's
 * name, the league and the registration (with `sign_up` as under a player
 * invitation, the fee quote only when the player would owe, and the
 * questions). Never an id, an email or another team (T16); the same body
 * for every caller (decision 19).
 */
export interface MemberTeamCodeLanding {
  team_code: {
    code: string
    /** `name` null for a team not named yet. */
    team: { name: string | null }
    captain: { name: string } | null
    league: { name: string | null }
    /** roster_max minus the team's roster count; null without a limit. */
    roster_spots_left: number | null
    /** The captain pays one team fee (the player owes nothing). */
    captain_pays: boolean
    registration: MemberRegistrationCard & {
      fee_quote: MemberFeeQuote | null
      questions: { standard: MemberStandardQuestion[]; custom: CustomFieldDef[] }
      /** Step 34: the attached waiver's current text, or null; optional for deploy skew. */
      waiver?: MemberWaiver | null
    }
  }
  timezone: string | null
}

/** POST /invites/accept, and the redeem part of a sign-up with invite_token: the new entry and its registration card. */
export interface MemberRedeemReply {
  entry: MemberEntry
  registration: MemberRegistrationCard
}

/** POST /registrations/:slug/entries. Names and email come from the server. */
export interface MemberSignUpBody {
  answers: Record<string, string | number | boolean>
  phone?: string
  /** Step 34: the signature, when the registration has a waiver. */
  waiver?: MemberWaiverSign
}

/**
 * A failed member call. `status` is 0 for a network error (code 'network')
 * or a timeout (code 'timeout').
 * `code` is the body's `code` (else its `error`), else a fallback by status.
 * `message` is the server's message or ''. Only the messages member-accounts.md
 * marks as written for display may be shown: validation_failed (`field` /
 * `fields`; since step 8 also on `current_password`, `new_password`,
 * `password`, `photo` and the profile fields), account_exists, no_profile,
 * registration_closed, not_open_yet, not_available_yet, registration_full,
 * already_registered, not_cancellable, and (step 12, the 409s of POST
 * /checkout) checkout_in_progress, payments_unavailable, already_paid and
 * nothing_to_pay, registration_full and roster_full (QA, October 30: no spot
 * left), spot_open and no_waitlist (POST /me/entries/:id/waitlist), and
 * (step 34, the waivers) waiver_changed (409, with the
 * new text in `waiver`), waiver_required (409 on the checkout),
 * no_waiver and entry_not_active (409 on a later signature). `reset_invalid` (410, an expired or
 * used reset link) maps to fixed copy, never `message`. So do the team-code
 * answers (step 26): `team_code_not_found` (404, a malformed, unknown,
 * replaced or another league's code) and `too_many_attempts` (429, the miss
 * limiter), and on the team-code join page every 409 that means "not now".
 * `fields` is a 400's `{ key: message }` map (every field at fault), or null.
 */
export class MemberApiError extends Error {
  readonly status: number
  readonly code: string
  readonly field: string | null
  readonly fields: Readonly<Record<string, string>> | null
  /** Step 34: the new text on a 409 waiver_changed (whitelisted), else null. */
  readonly waiver: MemberWaiver | null

  constructor(
    status: number,
    code: string,
    message: string,
    field: string | null = null,
    fields: Readonly<Record<string, string>> | null = null,
    waiver: MemberWaiver | null = null,
  ) {
    super(message)
    this.name = 'MemberApiError'
    this.status = status
    this.code = code
    this.field = field
    this.fields = fields
    this.waiver = waiver
  }
}

function fallbackCode(status: number): string {
  if (status === 0) return 'network'
  if (status === 429) return 'rate_limited'
  // The lockdown answers {reason} with no code.
  if (status === 503) return 'unavailable'
  // requireFeature answers {error:'feature_not_entitled'} with no code.
  if (status === 403) return 'feature_not_entitled'
  if (status === 404) return 'not_found'
  return 'server'
}

function errorFrom(status: number, body: unknown): MemberApiError {
  const b = body && typeof body === 'object' ? (body as Record<string, unknown>) : {}
  const code = typeof b.code === 'string' ? b.code : typeof b.error === 'string' ? b.error : fallbackCode(status)
  const message = typeof b.message === 'string' ? b.message : ''
  const field = typeof b.field === 'string' ? b.field : null
  // Step 34: only a 409 waiver_changed carries the new text, and only its whitelisted members are kept.
  const waiver = status === 409 && code === 'waiver_changed' ? parseWaiverText(b.waiver) : null
  return new MemberApiError(status, code, message, field, fieldsOf(b.fields), waiver)
}

/** A plain object's own string-valued entries, in a null-prototype map; else null. */
function fieldsOf(raw: unknown): Record<string, string> | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null
  const proto = Object.getPrototypeOf(raw)
  if (proto !== Object.prototype && proto !== null) return null
  const out = Object.create(null) as Record<string, string>
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof v === 'string') out[k] = v
  }
  return out
}

const sessionEndedListeners = new Set<() => void>()

/** Called when a member 401 ended the session (the token is already cleared). */
export function onMemberSessionEnded(fn: () => void): () => void {
  sessionEndedListeners.add(fn)
  return () => {
    sessionEndedListeners.delete(fn)
  }
}

function endMemberSession(): void {
  clearMemberToken()
  for (const fn of Array.from(sessionEndedListeners)) {
    try {
      fn()
    } catch {
      /* one listener's failure must not stop the others */
    }
  }
}

/**
 * End the member session for a token a public read (members-only league,
 * registration step 33) was refused with `session_ended`: only while that
 * token is still the stored one, so a newer sign-in (another tab) is never
 * cleared. Returns whether it ended the session.
 */
export function endMemberSessionFor(sentToken: string): boolean {
  if (getMemberToken() !== sentToken) return false
  endMemberSession()
  return true
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE'
  /** JSON, or a FormData sent as-is (the browser sets the multipart boundary). */
  body?: unknown
  /**
   * true: send the stored member token; a 401 then ends the member session.
   * 'optional': send it when one is stored, and never apply the 401 rule
   * (a public read that answers `session: 'ended'` instead).
   */
  auth?: boolean | 'optional'
  /** The client's own abort (default 30 s; the photo upload allows longer). */
  timeoutMs?: number
}

async function request<T>(path: string, { method = 'GET', body, auth = false, timeoutMs = TIMEOUT_MS }: RequestOptions = {}): Promise<T> {
  const sentToken = auth ? getMemberToken() : null
  const multipart = typeof FormData !== 'undefined' && body instanceof FormData
  const headers: Record<string, string> = {}
  // A FormData carries no Content-Type of ours: the browser adds the boundary.
  if (body !== undefined && !multipart) headers['Content-Type'] = 'application/json'
  if (auth && sentToken) headers['Authorization'] = `Bearer ${sentToken}`

  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), timeoutMs)
  let res: Response
  let data: unknown = null
  try {
    res = await fetch(`${BASE}${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : multipart ? (body as FormData) : JSON.stringify(body),
      cache: 'no-store',
      signal: controller.signal,
    })
    data = await res.json().catch(() => null)
  } catch {
    // Our own abort is the timeout (a slow server), not a lost connection.
    throw new MemberApiError(0, controller.signal.aborted ? 'timeout' : 'network', '')
  } finally {
    clearTimeout(timer)
  }

  if (res.ok) return data as T

  if (auth === true && res.status === 401) {
    const now = getMemberToken()
    if (now === sentToken || now === null) {
      endMemberSession()
      return new Promise<T>(() => {})
    }
  }
  throw errorFrom(res.status, data)
}

export const memberApi = {
  /** 201 {token, account, profile}. No Authorization header is sent. */
  signUp(input: MemberSignUpInput): Promise<MemberSession | MemberVerificationRequired> {
    return request<MemberSession | MemberVerificationRequired>('/auth/sign-up', { method: 'POST', body: input })
  },
  /** 200 {token, account, profile}; a 401 invalid_credentials is a plain error. */
  signIn(email: string, password: string): Promise<MemberSession | MemberVerificationRequired> {
    return request<MemberSession | MemberVerificationRequired>('/auth/sign-in', { method: 'POST', body: { email, password } })
  },
  /** POST /auth/verify-email (October 29): the emailed code signs the person in. */
  verifyEmail(email: string, code: string): Promise<MemberSession> {
    return request<MemberSession>('/auth/verify-email', { method: 'POST', body: { email, code } })
  },
  /** POST /auth/verify-email/send: a new code (the same answer for every address). */
  resendVerification(email: string): Promise<{ ok: true; message: string }> {
    return request('/auth/verify-email/send', { method: 'POST', body: { email } })
  },
  /** The signed-in member. A 401 ends the member session and never settles. */
  me(): Promise<{ account: MemberAccount; profile: MemberProfile | null }> {
    return request('/me', { auth: true })
  },
  /** The open registrations (cards). No token is ever sent. */
  registrations(): Promise<MemberRegistrationList> {
    return request<MemberRegistrationList>('/registrations')
  },
  /**
   * One registration. Sends the token when one is stored; never a 401. On
   * `session: 'ended'` the member session ends (only if the stored token is
   * still the one sent) and the call still resolves with the data.
   */
  async registration(slug: string): Promise<MemberRegistrationDetail> {
    const sent = getMemberToken()
    const data = await request<MemberRegistrationDetail>(`/registrations/${encodeURIComponent(slug)}`, { auth: 'optional' })
    if (data && data.session === 'ended' && sent && getMemberToken() === sent) endMemberSession()
    return data
  },
  /**
   * Register a team from the page (step 17, default 31): POST
   * /registrations/:slug/teams { team_name, email }. Never sends the member
   * token (captains are admin logins). The 201 is the same whether the
   * address has an account: { team: { name }, message }, never an id or a link.
   */
  registerTeam(slug: string, body: { team_name: string; email: string }): Promise<{ team: { name: string }; message: string }> {
    return request(`/registrations/${encodeURIComponent(slug)}/teams`, { method: 'POST', body })
  },
  /** Sign up. A 401 ends the member session and never settles. */
  register(slug: string, body: MemberSignUpBody): Promise<{ entry: MemberEntry; registration: MemberRegistrationCard }> {
    return request(`/registrations/${encodeURIComponent(slug)}/entries`, { method: 'POST', body, auth: true })
  },
  /**
   * The invitation landing (step 15): GET /invites?token=<encoded>. Never
   * sends the member token (the landing never reads it). The token is never
   * put in an error message or a log here.
   */
  invite(token: string): Promise<MemberInviteLanding> {
    return request<MemberInviteLanding>(`/invites?token=${encodeURIComponent(token)}`)
  },
  /** Redeem an invitation as the signed-in member, with the answers. A 401 ends the session and never settles. */
  acceptInvite(body: { token: string; answers?: Record<string, string | number | boolean>; phone?: string; waiver?: MemberWaiverSign }): Promise<MemberRedeemReply> {
    return request<MemberRedeemReply>('/invites/accept', { method: 'POST', body, auth: true })
  },
  /**
   * Create an account and redeem an invitation in one step (POST
   * /auth/sign-up with invite_token, T3: all or nothing). No Authorization
   * header. The caller stores the session (MemberAuthContext).
   */
  signUpWithInvite(
    input: MemberSignUpInput & { invite_token: string; answers?: Record<string, string | number | boolean>; phone?: string; waiver?: MemberWaiverSign }
  ): Promise<(MemberSession & MemberRedeemReply) | MemberVerificationRequired> {
    return request<(MemberSession & MemberRedeemReply) | MemberVerificationRequired>('/auth/sign-up', { method: 'POST', body: input })
  },
  /**
   * The team-code landing (step 25): GET /team-codes/<encoded code>. Never
   * sends the member token: the landing never reads one (decision 19).
   */
  teamCode(code: string): Promise<MemberTeamCodeLanding> {
    return request<MemberTeamCodeLanding>(`/team-codes/${encodeURIComponent(code)}`)
  },
  /** Join a team with its code as the signed-in member, with the answers. A 401 ends the session and never settles. */
  joinTeamCode(code: string, body: { answers?: Record<string, string | number | boolean>; phone?: string; waiver?: MemberWaiverSign }): Promise<MemberRedeemReply> {
    return request<MemberRedeemReply>(`/team-codes/${encodeURIComponent(code)}/join`, { method: 'POST', body, auth: true })
  },
  /**
   * Create an account and join a team with its code in one step (POST
   * /auth/sign-up with team_code, T3: all or nothing). No Authorization
   * header and never an invite_token (the server refuses both). The caller
   * stores the session (MemberAuthContext).
   */
  signUpWithTeamCode(
    input: MemberSignUpInput & { team_code: string; answers?: Record<string, string | number | boolean>; phone?: string; waiver?: MemberWaiverSign }
  ): Promise<(MemberSession & MemberRedeemReply) | MemberVerificationRequired> {
    return request<(MemberSession & MemberRedeemReply) | MemberVerificationRequired>('/auth/sign-up', { method: 'POST', body: input })
  },
  /** Cancel one of the member's own entries. A 401 ends the session. */
  cancelEntry(id: number): Promise<{ entry: MemberEntry }> {
    return request(`/me/entries/${encodeURIComponent(String(id))}`, { method: 'DELETE', auth: true })
  },
  /**
   * Step 34: sign the registration's waiver later (POST
   * /me/entries/:id/waiver { waiver }), for one of the member's own entries.
   * Already signed answers 200 unchanged. Never touches an order. A 401
   * ends the session.
   */
  signEntryWaiver(id: number, waiver: MemberWaiverSign): Promise<{ entry: MemberEntry }> {
    return request(`/me/entries/${encodeURIComponent(String(id))}/waiver`, { method: 'POST', body: { waiver }, auth: true })
  },
  /**
   * Step 36: accept an open waitlist offer (POST /me/entries/:id/accept).
   * 409 offer_expired / no_offer / team_not_active; the entry then moves on
   * (pay, waiver or registered, the server's next state). A 401 ends the
   * session.
   */
  acceptOffer(id: number): Promise<{ entry: MemberEntry }> {
    return request(`/me/entries/${encodeURIComponent(String(id))}/accept`, { method: 'POST', body: {}, auth: true })
  },
  /**
   * QA, October 30: join the waitlist instead of paying, once no spot is left
   * (POST /me/entries/:id/waitlist). 409 spot_open (a spot came back: pay) /
   * no_waitlist. A 401 ends the session.
   */
  joinWaitlist(id: number): Promise<{ entry: MemberEntry }> {
    return request(`/me/entries/${encodeURIComponent(String(id))}/waitlist`, { method: 'POST', body: {}, auth: true })
  },
  /** The member's entries (My account). A 401 ends the session. */
  myEntries(): Promise<{ entries: MemberEntry[] }> {
    return request('/me/entries', { auth: true })
  },
  /** The invitations waiting for this account (My account). A 401 ends the session. */
  myInvites(): Promise<MemberInvitationList> {
    return request('/me/invites', { auth: true })
  },
  /**
   * Pay for a registration (step 12): POST /checkout with exactly `{ slug }`
   * (the server derives the entries and the amount, registration.md quirk
   * 42). 201 a new order, 200 the open one reused. A 401 ends the session.
   */
  checkout(slug: string): Promise<{ order: MemberOrder; checkout_url: string }> {
    return request('/checkout', { method: 'POST', body: { slug }, auth: true })
  },
  /** One of the member's own orders (polled after Stripe). A 401 ends the session. */
  order(id: number): Promise<{ order: MemberOrder }> {
    return request(`/orders/${encodeURIComponent(String(id))}`, { auth: true, timeoutMs: 10_000 })
  },

  // ── Step 8's routes (My account and password recovery, step 9) ──────────

  /** Always 200 for any address; the body's message is never used (quirk 32). No token. */
  async forgotPassword(email: string): Promise<{ ok: true }> {
    await request('/auth/forgot', { method: 'POST', body: { email } })
    return { ok: true }
  },
  /** 200 {valid:true}; 410 reset_invalid for an expired, used or unknown link. No token. */
  async checkReset(token: string): Promise<{ valid: true }> {
    await request(`/auth/reset?token=${encodeURIComponent(token)}`)
    return { valid: true }
  },
  /** 200 {ok:true}; issues no session (member-accounts.md quirk 26). No token. */
  async resetPassword(token: string, password: string): Promise<{ ok: true }> {
    await request('/auth/reset', { method: 'POST', body: { token, password } })
    return { ok: true }
  },
  /** PUT /me (partial; '' or null clears phone and date of birth). A 401 ends the session. */
  updateMe(patch: MemberProfilePatch): Promise<{ account: MemberAccount; profile: MemberProfile }> {
    return request('/me', { method: 'PUT', body: patch, auth: true })
  },
  /**
   * PUT /me/password. Answers a fresh token for this browser, which the
   * caller (MemberAuthContext) stores; this client stores nothing. A wrong
   * current password is a 400 on `current_password`, never a 401 (quirk 25).
   */
  changePassword(current_password: string, new_password: string): Promise<{ token: string; account: MemberAccount }> {
    return request('/me/password', { method: 'PUT', body: { current_password, new_password }, auth: true })
  },
  /** POST /me/photo: one multipart part `photo`. A longer timeout (the server transcodes). */
  uploadPhoto(file: Blob): Promise<{ profile: MemberProfile }> {
    const fd = new FormData()
    if (typeof File !== 'undefined' && file instanceof File) fd.append('photo', file, file.name)
    else fd.append('photo', file)
    return request('/me/photo', { method: 'POST', body: fd, auth: true, timeoutMs: PHOTO_TIMEOUT_MS })
  },
}
